<template>
  <div class="is-relative">
    <StatusFilter />
    <DataTable
      :is_createable="false"
      :show_delete="false"
      title="Company Offers"
      @thFilter="thFilter"
      @edit_click="editClick"
      @internal_notes="clickedInternalNotes"
      @reject_offer="rejectOffer"
      @send_whatsapp="sendWhatsapp"
      @related_positions="relatedPositions"
      @text_search_results="textSearchResults"
      @position_modal_hover="positionModalHover"
      @position_modal_leave="positionModalLeave"
      @score="score"
      @ai_evaluate="aiEvaluate"
      @send_history="sendHistory"
      :unique_buttons="unique_buttons"
      :filters="filters"
      :sorters="sorters"
      :fields="fields"
      :counters="counters"
      :response="data"
      :operations="operations"
      :working_th_searches="working_th_searches"
      :pageTitleAdditionData="{ showGhanaFlag: true, company_self_row: true, sent_applicants: true }"
    />
    <div v-if="countLoading" class="offers-pagination-loading" style="text-align: center; padding: 16px;">
      <i class="fas fa-spinner fa-spin"></i>
    </div>
    <Pagination
      v-else-if="totalPages > 1"
      :totalPages="totalPages"
      :currentPage="currentPage"
      @page-change="handlePageChange"
      @per-page-change="handlePerPageChange"
    />
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from 'vuex'
import OffersHelper from '@/custom/classes/offers_helper.js'
import FiltersHelper from '@/custom/classes/filters_helper.js'
import DataTable from '@/components/DataTable.vue'
import StatusFilter from '@/components/StatusFilter.vue'
import SentCodesDisplay from '@/components/SentCodesDisplay.vue'
import Pagination from '@/components/shared/organisms/Pagination.vue'

export default {
  name: 'CompanyOffers',
  props: ['payload'],
  data () {
    return {
      offers_helper: new OffersHelper(),
      filters_helper: new FiltersHelper(),
      fields: [
        {
          name: 'sent_codes',
          component: 'SentCodesDisplay'
        },
        {
          name: 'full_name',
          component: 'LinkEntity',
          component_data_path: 'applicant'
        },
        {
          name: 'applicant_message',
          component: 'Message',
          component_data_path: 'applicant',
          additional_data: {
            endpoint: '/users/',
            headers: 'admin_headers',
            resource_type: 'Applicant',
            show_attribute_form: true
          }
        },
        {
          name: 'status',
          value: 'status.name',
          component: 'OfferStatus'
        },
        {
          name: 'offer_message',
          value: 'message',
          component: 'Message',
          additional_data: {
            endpoint: '/offers/',
            headers: 'admin_headers',
            resource_type: 'Offer'
          }
        },
        {
          name: 'distance',
          value: 'distance',
          component: 'OfferDistance'
        },
        {
          name: 'position_title',
          component: 'LinkEntity',
          component_data_path: 'position',
          additional_data: {
            resource_type: 'Position'
          }
        },
        {
          name: 'last_login',
          value: 'applicant.last_login',
          is_time: true
        },
        {
          name: 'updated_at',
          value: 'updated_at',
          is_time: true
        },
        {
          name: 'sent_at',
          value: 'sent_at',
          is_time: true
        },
        {
          name: 'applicant_id',
          value: 'applicant.resource_id'
        },
        {
          name: 'applicant_status',
          value: 'applicant.status'
        },
        {
          name: 'owner',
          value: 'owner'
        },
        {
          name: 'id',
          value: 'id'
        },
        {
          name: 'score',
          value: 'score'
        },
        {
          name: 'score_info',
          value: 'score_info'
        }
      ],
      filters: [],
      sorters: [
        {
          designator: 'updated_at',
          value: '-updated_at'
        },
        {
          designator: 'position_id',
          value: '-position_id'
        }
      ],
      data: [],
      counters: [],
      operations: [
        { component: 'Preview', additional_data: { resource_type: 'Offer' } },
        { name: 'reject_offer', icon: '/black_x.png' },
        { name: 'send_whatsapp', title: 'Send Offer WA' },
        { name: 'internal_notes', icon: '/note.png' },
        { name: 'related_positions', icon: '/magic_wand.png' },
        { component: 'OfferWhatsAppLink' },
        { name: 'position_modal', icon: '/applicant_modal.png' },
        {
          component: 'BoosterButton',
          additional_data: { resource_type: 'Offer' }
        },
        {
          component: 'ButtonEvaluate',
          additional_data: { resource_type: 'Offer' }
        },
        { component: 'OperationScore' },
        { name: 'ai_evaluate', title: 'AI' },
        { name: 'send_history', title: 'שליחות' }
      ],
      unique_buttons: [
        { name: 'score', display_text: 'Score' }
      ],
      company_id: this.$route.params.company_id || this.$route.query.company_id,
      th_filters: [],
      working_th_searches: ['full_name', 'position_title', 'owner'],
      per: 25,
      totalCount: 0,
      countLoading: false
    }
  },
  created: function () {
    this.init()
    this.fetchAll()
    this.fetchAllSkills()
    this.watchTreeShaking(this.getFiltersOptions, 'statuses')
  },
  computed: {
    ...mapGetters('modals', ['findModal']),
    ...mapGetters('admins', ['dropdownAdmins']),
    ...mapGetters('skills', ['dropdownSkills']),
    ...mapGetters('offerStatuses', ['getFiltersOptions', 'getAdvancedStatuses', 'getStarStatuses']),
    currentPage () {
      return parseInt(this.$route.query.page, 10) || 1
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.totalCount / this.per))
    }
  },
  watch: {
    dropdownAdmins (newValue) {
      this.watchTreeShaking(newValue, 'owner')
    },
    dropdownSkills (newValue) {
      this.watchTreeShaking(newValue, 'skills')
      this.watchTreeShaking(newValue, 'skills_exact')
    },
    getFiltersOptions (newValue) {
      this.watchTreeShaking(newValue, 'statuses')
    },
    '$route.query.statuses' () {
      // Reload data when status filter changes
      this.getOffers()
    },
    '$route.query.page' () {
      // Reload data when page changes
      this.getOffers()
    }
  },
  methods: {
    init () {
      this.getOffers()
    },
    sendWhatsapp (data) {
      this.create({
        designator: 'whatsapp-conversation-modal',
        component_name: 'ModalWhatsappMessager',
        modal_title: 'Conversation',
        props: {
          applicant: data.applicant,
          offer: data
        }
      })
    },
    watchTreeShaking (newValue, designator) {
      this.filters.forEach(filter => {
        if (filter.designator == designator) {
          filter.options = newValue
        }
      })
    },
    async getOffers () {
      this.data = []
      if (!this.company_id) {
        // eslint-disable-next-line no-console
        console.error('No company_id found in route')
        return
      }

      if (this.$route.query.applicant_text) {
        this.operations.push({ name: 'text_search_results', icon: '/applicant_text_search.png' })
        this.fields.splice(4, 0, {
          name: 'Applicant Text',
          value: 'applicant.resource_id',
          component: 'TextSearchResultContainer',
          css_classes: {
            'max-width-450': () => true
          }
        })
      }

      const query = this.getQueryParams()
      try {
        const response = await this.$network_utils.getCompanyOffers(query)
        this.data = response || []

        this.countLoading = true
        this.getCounters().then(response => {
          // A slow/timed-out count call can resolve with an empty body -> null here.
          // Never clobber a known count (and hide pagination) on a failed count fetch.
          if (response == null) return
          this.totalCount = response
          const existing = this.counters.find(c => c.name == 'result-counter')
          if (existing) {
            existing.value = response
          } else {
            this.counters.push({
              name: 'result-counter',
              label: 'Result counter',
              value: response
            })
          }
        }).catch(error => {
          // eslint-disable-next-line no-console
          console.error('Error loading offers counter:', error)
        }).then(() => {
          this.countLoading = false
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading company offers:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בטעינת ההצעות', type: 'error' })
      }
    },
    getCounters () {
      const query = this.getQueryParams()
      return this.$network_utils.getCompanyOffers({
        ...query,
        count_only: true,
        per: undefined,
        page: undefined
      }).then(response => {
        // New format: { count: N }
        if (response && typeof response.count === 'number') return response.count
        // Old format: array of offers
        if (Array.isArray(response)) return response.length
        // Empty/unknown body (e.g. slow count timed out at the gateway) -> unknown, keep prior count
        return null
      })
    },
    getQueryParams () {
      const sortCheck = this.$route.query.custom_sort ? this.$route.query.custom_sort : '-updated_at'

      // Set default statuses if none are specified
      let statuses = this.$route.query.statuses
      if (!statuses) {
        statuses = 'awaiting_company_sent,company_never_replied,received_proposal,interview,company_process'
      }

      return {
        company_id: this.company_id,
        ...this.filters_helper.getServerCallFilters(this.filters),
        sort: this.$route.query.sort || sortCheck,
        custom_sort: this.$route.query.custom_sort,
        per: this.per,
        page: this.$route.query.page || 1,
        statuses: statuses,
        activeness: this.$route.query.filter_by_active ? '50' : undefined,
        ...(this.$route.query.applicant_text && { applicant_text: encodeURIComponent(this.$route.query.applicant_text) }),
        applicant_tag: this.$route.query.applicant_tag,
        ...this.th_filters
      }
    },
    thFilter (th_filters) {
      this.th_filters = th_filters
      this.getOffers()
    },
    async score () {
      const answer = confirm('are you sure to score 1 hour?')
      if (answer) {
        await this.$network_utils.postEvent({
          event: 'resource_score',
          current_type: 'Company',
          current_resource_id: this.company_id
        })
        this.$utils.emit('show_toast', { text: 'נשלח בהצלחה' })
      }
    },
    relatedPositions (data) {
      this.create({
        designator: 'related-positions-modal',
        component_name: 'RelatedPositions',
        position: data.position,
        applicant: data.applicant,
        modal_title: data.position.title + ' - ' + (data.position.company_name || (data.position.company && data.position.company.name) || '') + ' - Related Positions '
      })
    },
    rejectOffer (data) {
      this.create({
        designator: 'reject-offer-modal-reason',
        component_name: 'MessageForm',
        resource_data: data,
        is_submitable: true,
        triggerBtn: true,
        additional_data: { endpoint: '/offers/', headers: 'admin_headers', resource_type: 'Offer' },
        modal_title: 'Reason for reject offer',
        props: {
          label: 'נא לפרט סיבה',
          onSubmit: id => {
            this.offers_helper.rejectOffer(data)
            this.removeModalById(id)
          }
        }
      })
    },
    textSearchResults (data) {
      this.$network_utils.getResourceTexts({
        current_type: 'Applicant',
        current_resource_id: data.applicant.resource_id
      }).then(response => {
        this.create({
          designator: `text-search-result-modal-${data.id}`,
          component_name: 'TextSearchResult',
          modal_title: 'Applicant Text Search Result',
          payload: data,
          props: {
            searchKeyword: this.$route.query.applicant_text,
            tags: data.applicant.tags || '',
            message: data.applicant.message || '',
            text: response,
            applicant: data.applicant
          }
        })
      })
    },
    clickedInternalNotes (data) {
      this.create({ designator: 'internal-notes-modal', component_name: 'InternalNotes', resource_type: 'Offer', resource_id: data.id, modal_title: 'הערות פנימיות' })
    },
    positionModalHover (data) {
      this.create({
        designator: 'position-info-modal',
        component_name: 'ModalPosition',
        modal_width: 1500,
        resource_type: 'Position',
        resource_id: data.position.id,
        modal_open_action: 'no-click',
        override_modal_x: 500,
        modal_title: data.position.title + (data.position.is_discrete ? ' (DISCRETE)' : ''),
        // עותק ולא המשרה עצמה — הוספת הקשר המועמד לא אמורה לשנות את השורה בטבלה.
        // applicant_id מפעיל את פירוט הכישורים מהשיחה בתוך המודל.
        data: { ...data.position, ...this.$utils.applicantModalContext(data.applicant) }
      })
    },
    positionModalLeave () {
      let position_info_modal = this.findModal({ type: 'designator', value: 'position-info-modal' })
      this.remove(position_info_modal)
    },
    aiEvaluate (data) {
      this.create({
        designator: 'ai-evaluation-modal-' + data.id,
        component_name: 'AiEvaluationModal',
        data,
        modal_title: 'הערכת AI',
        modal_width: 700,
        is_center_modal: true
      })
    },
    sendHistory (data) {
      this.create({
        designator: 'cv_send_history',
        component_name: 'CvSendHistoryModal',
        modal_title: 'היסטוריית שליחות',
        modal_width: 900,
        is_center_modal: true,
        props: { offer: data }
      })
    },
    handlePageChange (page) {
      if (page == this.currentPage) return
      this.$router.push({ name: this.$route.name, params: this.$route.params, query: { ...this.$route.query, page } })
    },
    handlePerPageChange (per) {
      this.per = per
      // reset to first page; getOffers runs via page watcher, or directly if already page 1
      if ((parseInt(this.$route.query.page, 10) || 1) != 1) {
        this.$router.push({ name: this.$route.name, params: this.$route.params, query: { ...this.$route.query, page: 1 } })
      } else {
        this.getOffers()
      }
    },
    editClick (data) {
      this.create({
        designator: 'company-offers-form-modal',
        component_name: 'PositionOffersForm',
        data,
        company_id: this.company_id,
        modal_title: 'Edit Offer'
      })
    },
    ...mapMutations('modals', ['create', 'removeModalById', 'remove']),
    ...mapActions('admins', ['fetchAll']),
    ...mapActions('skills', ['fetchAllSkills'])
  },
  components: {
    DataTable,
    StatusFilter,
    Pagination,
    // eslint-disable-next-line vue/no-unused-components
    SentCodesDisplay
  }
}
</script>
